import { parseInstagramUrl } from './instagramUrl';
import { extractFacebookVideoId, isFacebookUrl } from './facebookDisplay';

const INSTAGRAM_LEGACY_SHORTCODE = /^instagram:[A-Za-z0-9_-]+$/;

export function normalizeInstagramPk(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    const str = String(value).trim();
    return /^\d+$/.test(str) ? str : undefined;
}

export function normalizeInstagramMediaId(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    const str = String(value).trim();
    if (/^\d+$/.test(str)) return str;
    if (/^\d+_\d+$/.test(str)) return str;
    return undefined;
}

export function mediaPkFromCompositeId(compositeId: string): string | undefined {
    const [mediaPart] = compositeId.split('_');
    return normalizeInstagramPk(mediaPart);
}

export function formatInstagramExternalVideoIdFromPk(pk: string): string {
    return `instagram:${pk.trim()}`;
}

export function formatInstagramExternalVideoIdFromMediaId(mediaId: string): string {
    return `instagram:${mediaId.trim()}`;
}

export function formatInstagramExternalVideoIdFromShortCode(shortCode: string): string {
    return `instagram:code:${shortCode.trim()}`;
}

export function formatFacebookExternalVideoId(videoId: string): string {
    return `facebook:${videoId.trim()}`;
}

export function buildInstagramExternalVideoIdAliases(params: {
    mediaPk?: string;
    mediaId?: string;
    shortCode?: string;
}): string[] {
    const aliases = new Set<string>();
    const compositeId = params.mediaId?.includes('_') ? params.mediaId : undefined;
    const pk = params.mediaPk
        ?? (compositeId ? mediaPkFromCompositeId(compositeId) : undefined);

    if (pk) {
        aliases.add(formatInstagramExternalVideoIdFromPk(pk));
    }
    if (compositeId) {
        aliases.add(formatInstagramExternalVideoIdFromMediaId(compositeId));
    } else if (params.mediaId && params.mediaId !== pk) {
        aliases.add(formatInstagramExternalVideoIdFromMediaId(params.mediaId));
    }
    if (params.shortCode) {
        aliases.add(formatInstagramExternalVideoIdFromShortCode(params.shortCode));
        aliases.add(`instagram:${params.shortCode.trim()}`);
    }

    return [...aliases];
}

export function pickPrimaryInstagramExternalVideoId(aliases: readonly string[]): string | null {
    const pkAlias = aliases.find((alias) => /^instagram:\d+$/.test(alias));
    if (pkAlias) return pkAlias;

    const compositeAlias = aliases.find((alias) => /^instagram:\d+_\d+$/.test(alias));
    if (compositeAlias) return compositeAlias;

    const codeAlias = aliases.find((alias) => alias.startsWith('instagram:code:'));
    if (codeAlias) return codeAlias;

    const legacyAlias = aliases.find((alias) => INSTAGRAM_LEGACY_SHORTCODE.test(alias));
    return legacyAlias ?? aliases[0] ?? null;
}

export function buildInstagramExternalVideoIds(params: {
    mediaPk?: string;
    mediaId?: string;
    shortCode?: string;
}): { externalVideoId: string | null; externalVideoIdAliases: string[] } {
    const externalVideoIdAliases = buildInstagramExternalVideoIdAliases(params);
    return {
        externalVideoId: pickPrimaryInstagramExternalVideoId(externalVideoIdAliases),
        externalVideoIdAliases,
    };
}

export function resolveExternalVideoIdFromUrls(urls: readonly (string | undefined)[]): string | null {
    const aliases = resolveExternalVideoIdAliasesFromUrls(urls);
    return pickPrimaryInstagramExternalVideoId(aliases) ?? aliases[0] ?? null;
}

export function resolveExternalVideoIdAliasesFromUrls(urls: readonly (string | undefined)[]): string[] {
    const aliases = new Set<string>();

    for (const url of urls) {
        if (!url?.trim()) continue;

        const trimmed = url.trim();
        const instagram = parseInstagramUrl(trimmed);
        if (instagram?.shortCode) {
            buildInstagramExternalVideoIdAliases({ shortCode: instagram.shortCode })
                .forEach((alias) => aliases.add(alias));
            continue;
        }

        const facebookVideoId = extractFacebookVideoId(trimmed);
        if (facebookVideoId) {
            aliases.add(formatFacebookExternalVideoId(facebookVideoId));
        }
    }

    return [...aliases];
}

export function collectRecipeExternalVideoKeys(recipe: {
    externalVideoId?: string;
    externalVideoIdAliases?: string[];
}): Set<string> {
    const keys = new Set<string>();
    if (recipe.externalVideoId?.trim()) keys.add(recipe.externalVideoId.trim());
    recipe.externalVideoIdAliases?.forEach((alias) => {
        if (alias.trim()) keys.add(alias.trim());
    });
    return keys;
}

export function externalVideoKeysOverlap(
    left: readonly string[],
    right: readonly string[],
): boolean {
    const rightSet = new Set(right);
    return left.some((key) => rightSet.has(key));
}

export function isResolvableExternalVideoUrl(url: string): boolean {
    const trimmed = url.trim();
    if (!trimmed) return false;
    if (parseInstagramUrl(trimmed)) return true;
    if (extractFacebookVideoId(trimmed)) return true;
    return isFacebookUrl(trimmed);
}
